import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ShoppingBag, Star, Shield, Truck, CheckCircle2, Lock, Sparkles, Gift } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { PayPalButton } from '../components/PayPalButton';
import { StripeButton } from '../components/StripeButton';
import { PaymentTrustBlock } from '../components/PaymentTrustBlock';
import { BundleUpsell } from '../components/BundleUpsell';
import { boyshortsProductData } from '../data/products';
import { useSEO } from '../hooks/useSEO';
import { useJsonLd, breadcrumbSchema } from '../hooks/useJsonLd';

export const BoyshortsPage = () => {
  useSEO({
    title: 'Seamless Smoothing Boyshorts — Free With Bundle | CurveeForYou',
    description: 'Soft, invisible second-skin smoothing boyshorts. Yours FREE with the $89.99 Snatched Duo Bundle + free US shipping + 30-day guarantee.',
    canonical: 'https://curveeforyou.com/boyshorts'
  });

  // Informational page — the Seamless Boyshorts are only sold as a free gift with the $89.99 Snatched Duo Bundle.
  // Product schema lives on the homepage (BundlePage) to avoid conflicting price data in Google Shopping.
  useJsonLd([
    breadcrumbSchema([
      { name: 'Home', url: '/' },
      { name: 'Seamless Boyshorts', url: '/boyshorts' }
    ])
  ]);

  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState(boyshortsProductData.colors[0]);
  const [quantity] = useState(1);

  const handleBuyNow = () => {
    // Bundle-only store — redirect to the bundle homepage
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-50/40 to-white">
      {/* Limited Time Banner — matches Bundle landing */}
      <div className="bg-gradient-to-r from-[#d4af37] via-[#c49d2a] to-[#d4af37] text-[#5d1729] py-3 px-4">
        <div className="container mx-auto flex items-center justify-center gap-2 text-center">
          <Gift className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm md:text-lg font-bold uppercase tracking-wide">For A Limited Time — Snatched From Bust To Thigh: Buy 2 Fajas, Get FREE Boyshorts</span>
          <Gift className="w-5 h-5 flex-shrink-0" />
        </div>
      </div>

      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/" data-testid="boyshorts-nav-logo">
            <img
              src="https://customer-assets.emergentagent.com/job_item-landing/artifacts/feqbyn90_Logo%20curveeforyou.png"
              alt="CurveeForYou"
              className="h-20 md:h-24 w-auto"
            />
          </Link>
          <div className="hidden md:flex items-center gap-6 lg:gap-8">
            <Link to="/full-body-faja#reviews" className="text-gray-700 hover:text-[#7d1f3d] font-medium" data-testid="boyshorts-nav-reviews-link">
              Reviews
            </Link>
            <Link to="/full-body-faja#guarantee" className="text-gray-700 hover:text-[#7d1f3d] font-medium" data-testid="boyshorts-nav-guarantee-link">
              Guarantee
            </Link>
            <Link to="/about" className="text-gray-700 hover:text-[#7d1f3d] font-medium" data-testid="boyshorts-nav-about-link">
              About
            </Link>
            <Link to="/" data-testid="boyshorts-nav-bundle-cta">
              <Button className="bg-[#7d1f3d] hover:bg-[#5d1729] text-white px-6">
                <ShoppingBag className="w-4 h-4 mr-2" /> Buy Now
              </Button>
            </Link>
          </div>
          <Link to="/" className="md:hidden">
            <Button variant="outline" size="sm" className="border-[#7d1f3d] text-[#7d1f3d]">
              <ArrowLeft className="w-4 h-4" />
            </Button>
          </Link>
        </div>
      </header>

      <section className="container mx-auto px-5 py-8 md:py-10 max-w-6xl">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-start">

          {/* Left: image */}
          <div className="lg:col-span-6">
            <div className="bg-gradient-to-br from-rose-50 to-white rounded-2xl overflow-hidden shadow-xl aspect-[4/5] flex items-center justify-center p-8">
              <img
                src={boyshortsProductData.images[0]}
                alt="Seamless second-skin smoothing boyshorts in nude — lightweight, invisible-under-clothing everyday shapewear with smooth hem finish"
                className="w-full h-full object-contain"
              />
            </div>
          </div>

          {/* Right: details + checkout */}
          <div className="lg:col-span-6 space-y-5">
            <Badge className="bg-[#d4af37] text-[#5d1729] font-bold px-3 py-1 text-xs uppercase tracking-wider">
              Easiest first step into shapewear
            </Badge>
            <h1 className="text-3xl md:text-4xl font-bold text-[#7d1f3d] leading-[1.15]" data-testid="boyshorts-h1">
              {boyshortsProductData.name}
            </h1>
            <p className="text-gray-700 text-base md:text-lg leading-relaxed">
              {boyshortsProductData.subtitle}
            </p>

            {/* Rating */}
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-gray-600">
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={`star-${i}`} className="w-4 h-4 fill-[#d4af37] text-[#d4af37]" />
                ))}
                <span className="ml-1 font-semibold text-gray-700">{boyshortsProductData.rating}/5</span>
                <span className="text-gray-500">({boyshortsProductData.reviewCount.toLocaleString()})</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Truck className="w-4 h-4 text-[#7d1f3d]" /> Free US shipping
              </div>
              <div className="flex items-center gap-1.5">
                <Shield className="w-4 h-4 text-[#7d1f3d]" /> 30-day guarantee
              </div>
            </div>

            {/* Bundle-only CTA */}
            <div className="bg-gradient-to-br from-[#7d1f3d] to-[#5d1729] text-white rounded-xl p-5 shadow-lg">
              <p className="text-[#d4af37] text-xs font-bold uppercase tracking-wider mb-1">Bundle Exclusive</p>
              <p className="text-white font-bold text-lg mb-3">Included FREE with the Snatched Duo Bundle</p>
              <Link
                to="/"
                data-testid="boyshorts-bundle-cta"
                className="block w-full bg-[#d4af37] hover:bg-[#c19b2f] text-[#5d1729] text-center font-bold py-3 rounded-lg transition-colors"
              >
                Get the Bundle — $89.99 →
              </Link>
              <p className="text-white/80 text-xs mt-3 text-center">2 Fajas + free boyshorts · Free US shipping · 30-day guarantee</p>
            </div>

            <div className="space-y-2 pt-1 text-sm">
              <div className="flex items-start gap-2.5">
                <CheckCircle2 className="w-4 h-4 text-[#7d1f3d] flex-shrink-0 mt-0.5" />
                <p className="text-gray-700"><strong>Seamless &amp; invisible</strong> under any outfit</p>
              </div>
              <div className="flex items-start gap-2.5">
                <CheckCircle2 className="w-4 h-4 text-[#7d1f3d] flex-shrink-0 mt-0.5" />
                <p className="text-gray-700"><strong>Soft second-skin feel</strong> — zero learning curve</p>
              </div>
              <div className="flex items-start gap-2.5">
                <CheckCircle2 className="w-4 h-4 text-[#7d1f3d] flex-shrink-0 mt-0.5" />
                <p className="text-gray-700"><strong>Sizes S/M to 3XL/4XL</strong>, Black &amp; Nude</p>
              </div>
              <div className="flex items-start gap-2.5">
                <Sparkles className="w-4 h-4 text-[#d4af37] flex-shrink-0 mt-0.5" />
                <p className="text-gray-700"><strong>Gift idea:</strong> get them <Link to="/" className="text-[#7d1f3d] font-bold underline">free with the Snatched Duo Bundle</Link></p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Checkout section */}
      <section className="container mx-auto px-5 pb-14 max-w-3xl">
        <div className="bg-white border border-rose-100 rounded-2xl shadow-lg p-6 md:p-8 space-y-6">
          {/* Bundle-only CTA (replaces individual checkout) */}
          <div id="payment-section" className="pt-2">
            <Link
              to="/"
              data-testid="boyshorts-checkout-bundle-cta"
              className="block bg-gradient-to-br from-[#7d1f3d] to-[#5d1729] text-white rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-shadow"
            >
              <p className="text-[#d4af37] text-xs font-bold uppercase tracking-wider mb-2">Bundle Exclusive</p>
              <p className="text-white font-bold text-xl mb-1">The Seamless Boyshorts are included FREE</p>
              <p className="text-white/80 text-sm mb-4">Only available with the Snatched Duo Bundle — 2 Fajas + FREE Boyshorts.</p>
              <div className="bg-[#d4af37] text-[#5d1729] text-center font-bold py-3 rounded-lg">
                Get the Bundle — $89.99 →
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Mobile sticky */}
      <div
        className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-[0_-4px_12px_rgba(0,0,0,0.08)] px-4 py-3"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 12px)' }}
      >
        <Link
          to="/"
          data-testid="boyshorts-mobile-sticky-bundle"
          className="block w-full bg-[#7d1f3d] hover:bg-[#5d1729] text-white font-bold text-base text-center py-4 rounded-lg shadow-lg"
        >
          Get the Bundle — $89.99 →
        </Link>
      </div>
    </div>
  );
};

export default BoyshortsPage;
